import type {
    MetadataChange,
    PromptBlockKind,
    PromptDiffBlock,
    VersionComparison,
} from "./llmClassifierVersionComparison";

export interface GroupedDiff {
    key: string;
    label: string;
    beforeText: string;
    afterText: string;
    addedLines: number;
    removedLines: number;
}

export interface ComparisonGroup {
    key: string;
    /** Step name, or the implementation label for changes owned by no step. */
    title: string;
    /** 1-based chain position; undefined for the model group. */
    stepNumber?: number;
    /** Only set for a step that was added, removed or renamed. */
    kind?: PromptBlockKind;
    diffs: GroupedDiff[];
    metadata: MetadataChange[];
    addedLines: number;
    removedLines: number;
}

/** Keyed by step id, not name (which isn't unique); the "step:" prefix keeps it from colliding with the model's key. */
const IMPLEMENTATION_KEY = "model";
const IMPLEMENTATION_TITLE = "Model";

/** Regroups a comparison by step, so everything about one step reads as a unit instead of three separate sections. */
export function groupVersionComparison(
    comparison: VersionComparison,
): ComparisonGroup[] {
    const groups = new Map<string, ComparisonGroup>();

    // Takes the raw label so the key and title are derived together, not repeated per caller.
    const groupFor = (
        stepId?: string,
        label?: string,
        stepNumber?: number,
        kind?: PromptBlockKind,
    ) => {
        const key =
            stepId === undefined ? IMPLEMENTATION_KEY : `step:${stepId}`;
        const existing = groups.get(key);
        if (existing) {
            existing.kind ??= kind;
            existing.stepNumber ??= stepNumber;
            return existing;
        }

        const created: ComparisonGroup = {
            key,
            title: label ?? IMPLEMENTATION_TITLE,
            stepNumber,
            kind,
            diffs: [],
            metadata: [],
            addedLines: 0,
            removedLines: 0,
        };
        groups.set(key, created);
        return created;
    };

    for (const block of comparison.promptBlocks) {
        // Shared with plain metadata changes, so its kind shouldn't label the group.
        const kind = block.stepId === undefined ? undefined : block.kind;
        addDiff(groupFor(block.stepId, block.label, block.stepNumber, kind), promptDiff(block));
    }

    for (const block of comparison.configBlocks) {
        // Unlike a prompt block, a config block already carries every GroupedDiff field as-is.
        addDiff(
            groupFor(block.stepId, block.stepLabel, block.stepNumber),
            block,
        );
    }

    for (const change of comparison.metadataChanges) {
        groupFor(
            change.stepId,
            change.stepLabel,
            change.stepNumber,
        ).metadata.push(change);
    }

    // Sorted by stepNumber, not insertion order; undefined sorts to 0 so the model group leads.
    return [...groups.values()].sort(
        (left, right) => (left.stepNumber ?? 0) - (right.stepNumber ?? 0),
    );
}

function promptDiff(block: PromptDiffBlock): GroupedDiff {
    return {
        key: block.key,
        label: "Prompt changes",
        beforeText: block.beforeText,
        afterText: block.afterText,
        addedLines: block.addedLines,
        removedLines: block.removedLines,
    };
}

function addDiff(group: ComparisonGroup, diff: GroupedDiff) {
    group.diffs.push(diff);
    group.addedLines += diff.addedLines;
    group.removedLines += diff.removedLines;
}
